import React, { useState, useEffect } from 'react';
import { Modal } from '../../components/ui';
import { errMsg } from '../../utils/format';
import api from '../../utils/api';

const FEED_FIELDS_DEFAULT = [
  'itemid','upccode','description','model_number','list_price','map_mrp',
  'weight','net_cost','qoh','path_to_image','full_description','prop65',
  'sale_start','sale_end','regular_price','expected_date','condition',
];

const SF_FIELDS = [
  { value: 'quantity_on_hand',  label: 'Warehouse Qty' },
  { value: 'unit_price',        label: 'Price / MAP' },
  { value: 'compare_at_price',  label: 'Compare-at Price' },
  { value: 'unit_cost',         label: 'Unit Cost' },
  { value: 'weight',            label: 'Weight' },
  { value: 'image_url',         label: 'Image URL' },
  { value: 'description',       label: 'Description (HTML)' },
  { value: 'sku',               label: 'SKU' },
  { value: 'name',              label: 'Product Name' },
  { value: 'notes',             label: 'Notes' },
  { value: 'amazon_asin',       label: 'Amazon ASIN' },
];

const SF_MATCH_FIELDS = ['sku','name','amazon_asin'];

const blank = {
  name: '', enabled: true, format: 'csv', url: '', apiKey: '',
  authMethod: 'query_param', authParamName: 'apikey', authHeaderName: 'Authorization',
  syncTime: '02:00', jsonPath: '', matchKeyFeedField: 'itemid', matchKeySfField: 'sku',
  fieldMappings: [],
};

export default function FeedModal({ feed, onClose, onSaved }) {
  const [form, setForm] = useState(feed ? {
    name: feed.name, enabled: feed.enabled, format: feed.format,
    url: feed.url, apiKey: feed.api_key || '', authMethod: feed.auth_method,
    authParamName: feed.auth_param_name, authHeaderName: feed.auth_header_name,
    syncTime: feed.sync_time, jsonPath: feed.json_path || '',
    matchKeyFeedField: feed.match_key_feed_field, matchKeySfField: feed.match_key_sf_field,
    fieldMappings: feed.field_mappings || [],
  } : { ...blank });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addMapping = () => setForm(f => ({
    ...f, fieldMappings: [...f.fieldMappings, {
      id: Date.now().toString(), feedField: '', sfField: '', pushToShopify: false, enabled: true, condition: 'always', conditionValue: ''
    }]
  }));
  const removeMapping = (id) => setForm(f => ({ ...f, fieldMappings: f.fieldMappings.filter(m => m.id !== id) }));
  const updateMapping = (id, k, v) => setForm(f => ({
    ...f, fieldMappings: f.fieldMappings.map(m => m.id === id ? { ...m, [k]: v } : m)
  }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.url) { alert('URL is required'); return; }
    setSaving(true);
    try {
      if (feed) {
        await api.put(`/api/integrations/feeds/${feed.id}`, form);
      } else {
        await api.post('/api/integrations/feeds', form);
      }
      onSaved();
      onClose();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  // Guess feed fields from format for the dropdown helper
  const feedFields = FEED_FIELDS_DEFAULT;

  return (
    <Modal title={feed ? `Edit Feed — ${feed.name}` : 'New Third Party Feed'} onClose={onClose} wide
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="feed-form" type="submit">{saving ? 'Saving…' : 'Save feed'}</button></>}>
      <form id="feed-form" onSubmit={save}>

        {/* General */}
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>General</div>
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Feed name *</label>
            <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Wintron Electronics" />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Format</label>
            <select value={form.format} onChange={e => set('format', e.target.value)}>
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="xlsx">XLSX</option>
            </select>
          </div>
          <div className="form-group" style={{ flex: 0, minWidth: 110 }}>
            <label>Sync time</label>
            <input type="time" value={form.syncTime} onChange={e => set('syncTime', e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: 0, minWidth: 80, paddingTop: 22 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 'normal' }}>
              <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} style={{ width: 'auto' }} />
              Enabled
            </label>
          </div>
        </div>

        {/* URL & Auth */}
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, marginTop: 16 }}>Connection</div>
        <div className="form-group">
          <label>URL *</label>
          <input required value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://..." />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Authentication method</label>
            <select value={form.authMethod} onChange={e => set('authMethod', e.target.value)}>
              <option value="none">None</option>
              <option value="query_param">Query parameter</option>
              <option value="header">HTTP header</option>
            </select>
          </div>
          {form.authMethod !== 'none' && (
            <div className="form-group">
              <label>{form.authMethod === 'query_param' ? 'Parameter name' : 'Header name'}</label>
              <input
                value={form.authMethod === 'query_param' ? form.authParamName : form.authHeaderName}
                onChange={e => set(form.authMethod === 'query_param' ? 'authParamName' : 'authHeaderName', e.target.value)}
                placeholder={form.authMethod === 'query_param' ? 'apikey' : 'Authorization'}
              />
            </div>
          )}
          {form.authMethod !== 'none' && (
            <div className="form-group">
              <label>API key / token</label>
              <input value={form.apiKey} onChange={e => set('apiKey', e.target.value)} placeholder="Your key" />
            </div>
          )}
        </div>

        {form.format === 'json' && (
          <div className="form-group">
            <label>JSON path to items array <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(leave blank if root is the array)</span></label>
            <input value={form.jsonPath} onChange={e => set('jsonPath', e.target.value)} placeholder="e.g. data.products or response.items" />
          </div>
        )}

        {/* Match key */}
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, marginTop: 16 }}>Match Key</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: 11 }}>Feed field</label>
            <input value={form.matchKeyFeedField} onChange={e => set('matchKeyFeedField', e.target.value)} placeholder="e.g. itemid" />
          </div>
          <div style={{ textAlign: 'center', fontSize: 18, color: 'var(--text-tertiary)', paddingTop: 16 }}>→</div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: 11 }}>StockForge field</label>
            <select value={form.matchKeySfField} onChange={e => set('matchKeySfField', e.target.value)}>
              {SF_MATCH_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>

        {/* Field mappings */}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 20, marginBottom: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Field Mappings</div>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-sm" onClick={addMapping}>
            <i className="ti ti-plus" /> Add mapping
          </button>
        </div>
        <table style={{ width: '100%' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign:'left', padding:'4px 6px', fontSize:11, color:'var(--text-tertiary)' }}>Feed field</th>
              <th style={{ textAlign:'left', padding:'4px 6px', fontSize:11, color:'var(--text-tertiary)' }}>StockForge field</th>
              <th style={{ textAlign:'left', padding:'4px 6px', fontSize:11, color:'var(--text-tertiary)' }}>Condition</th>
              <th style={{ textAlign:'center', padding:'4px 6px', fontSize:11, color:'var(--text-tertiary)' }}>→ Shopify</th>
              <th style={{ textAlign:'center', padding:'4px 6px', fontSize:11, color:'var(--text-tertiary)' }}>On</th>
              <th style={{ width:36 }}></th>
            </tr>
          </thead>
          <tbody>
            {form.fieldMappings.map(m => (
              <tr key={m.id} style={{ borderBottom:'1px solid var(--border)', opacity: m.enabled ? 1 : 0.45 }}>
                <td style={{ padding:'4px 6px' }}>
                  <input value={m.feedField} onChange={e => updateMapping(m.id,'feedField',e.target.value)}
                    placeholder="Feed column name" list="feed-field-list" style={{ width:'100%' }} />
                  <datalist id="feed-field-list">
                    {feedFields.map(f => <option key={f} value={f} />)}
                  </datalist>
                </td>
                <td style={{ padding:'4px 6px' }}>
                  <select value={m.sfField} onChange={e => updateMapping(m.id,'sfField',e.target.value)} style={{ width:'100%' }}>
                    <option value="">— Select —</option>
                    {SF_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </td>
                <td style={{ padding:'4px 6px' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <select value={m.condition || 'always'}
                      onChange={e => updateMapping(m.id,'condition',e.target.value)}
                      style={{ width: 110 }}>
                      <option value="always">Always</option>
                      <option value="not_empty">Not empty</option>
                      <option value="gt">{'>'} value</option>
                      <option value="gte">{'>='} value</option>
                      <option value="lt">{'<'} value</option>
                      <option value="lte">{'<='} value</option>
                    </select>
                    {['gt','gte','lt','lte'].includes(m.condition) && (
                      <input type="number" step="any"
                        value={m.conditionValue || ''}
                        onChange={e => updateMapping(m.id,'conditionValue',e.target.value)}
                        style={{ width: 70 }} placeholder="0" />
                    )}
                  </div>
                </td>
                <td style={{ padding:'4px 6px', textAlign:'center' }}>
                  <input type="checkbox" checked={m.pushToShopify}
                    onChange={e => updateMapping(m.id,'pushToShopify',e.target.checked)} style={{ width:'auto' }} />
                </td>
                <td style={{ padding:'4px 6px', textAlign:'center' }}>
                  <input type="checkbox" checked={m.enabled}
                    onChange={e => updateMapping(m.id,'enabled',e.target.checked)} style={{ width:'auto' }} />
                </td>
                <td style={{ padding:'4px 6px' }}>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => removeMapping(m.id)}>
                    <i className="ti ti-x" />
                  </button>
                </td>
              </tr>
            ))}
            {!form.fieldMappings.length && (
              <tr><td colSpan={5} style={{ padding:'12px 6px', textAlign:'center', color:'var(--text-tertiary)', fontSize:12 }}>
                No mappings — click "Add mapping"
              </td></tr>
            )}
          </tbody>
        </table>
      </form>
    </Modal>
  );
}
